import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { ClientError } from "graphql-request";

import { graphql } from "lib/generated/app-service-gql";
import {
  BidResponseQuery,
  BidResponsesQuery,
  UpdateBidResponsesInput,
  UpdateBidResponsesMutation,
} from "lib/generated/app-service-gql/graphql";
import { EventNames, useTracking } from "lib/tracking";
import { isDefined } from "lib/utils";
import { useGraphQlMutation } from "../../useGraphQlClient";
import { updateBidQuery } from "../bids/updateBidCache";
import { BidDetails } from "../bids/useBid";

const getChangedField = (input: UpdateBidResponsesInput): string => {
  if (input.title) return "Title";
  if (input.description) return "Question";
  if (input.stageId) return "Status";
  if (input.response) return "Response text";
  if (input.weighting) return "Weighting";
  if (input.formattingRequirements) return "Formatting requirements";
  if (input.assigneeId) return "Assignee";
  if (input.reviewerId) return "Reviewer";
  // Checked against undefined, not truthiness, so clearing a due date still tracks.
  if (input.dueDate !== undefined) return "Due date";
  return "";
};

const UPDATE_BID_RESPONSES = graphql(`
  mutation UpdateBidResponses($input: UpdateBidResponsesInput!) {
    updateBidResponses(UpdateBidResponsesInput: $input) {
      id
      bidId
      reference
      title
      section
      description
      weighting
      formattingRequirements
      response
      teamId
      companyId
      stageId
      assignee {
        guid
        firstName
        lastName
      }
      reviewer {
        guid
        firstName
        lastName
      }
      dueDate
      createdAt
      updatedAt
      deletedAt
      stage {
        name
      }
    }
  }
`);

export function useUpdateBidResponses(
  options?: UseMutationOptions<
    UpdateBidResponsesMutation,
    ClientError,
    { input: UpdateBidResponsesInput },
    unknown
  >,
) {
  const queryClient = useQueryClient();
  const { logEvent } = useTracking();

  const { data, ...rest } = useGraphQlMutation(UPDATE_BID_RESPONSES, {
    ...options,
    onSuccess: async (data, variables, context) => {
      const { input } = variables;

      const updatedResponseById = new Map(data.updateBidResponses.map((r) => [r.id, r]));

      // Group by  bidId for detail-cache updates.

      const byBid: Record<string, typeof data.updateBidResponses> = {};

      for (const resp of data.updateBidResponses) {
        if (resp.bidId) {
          if (!byBid[resp.bidId]) byBid[resp.bidId] = [];
          byBid[resp.bidId].push(resp);
        }
      }

      const allBidIds = Object.keys(byBid);
      const allBidResponseIds = data.updateBidResponses.map((r) => r.id);

      // Update any cached bidResponses lists (regardless of scope/pagination/filters).
      queryClient.setQueriesData<BidResponsesQuery>(
        { queryKey: ["bidResponses"], exact: false },
        (oldData) => {
          if (!oldData?.bidResponses) return oldData;

          const existing = oldData.bidResponses.items;

          const items = existing
            .map((item) => {
              const updated = updatedResponseById.get(item.id);
              return updated ? { ...item, ...updated } : item;
            })
            .filter(isDefined);

          return {
            ...oldData,
            bidResponses: {
              ...oldData.bidResponses,
              items,
            },
          };
        },
      );

      // Keep bid detail caches in sync.
      for (const [bidId, updatedResponses] of Object.entries(byBid)) {
        updateBidQuery(queryClient, bidId, (bid) => {
          if (!bid) return bid;

          const updated: BidDetails["bidResponses"] = bid.bidResponses
            ?.map((item) => {
              const resp = updatedResponses.find((r) => r.id === item.id);
              return resp ? { ...item, ...resp } : item;
            })
            .filter(isDefined);

          return {
            ...bid,
            bidResponses: updated,
          };
        });
      }

      for (const resp of data.updateBidResponses) {
        queryClient.setQueryData<BidResponseQuery>(["bidResponse", resp.id], (prevData) => {
          if (!prevData?.bidResponse) return prevData;
          return { ...prevData, bidResponse: { ...prevData.bidResponse, ...resp } };
        });
      }

      logEvent(EventNames.bidResponseUpdated, {
        "Field updated": getChangedField(input),
        "Bid GUID(s)": allBidIds,
        "Bid response GUIDs": allBidResponseIds,
      });

      options?.onSuccess?.(data, variables, context);
    },
  });

  return { data: data?.updateBidResponses, ...rest };
}
